// Normalizes Django REST Framework error responses into a consistent shape.
//
// DRF can return errors as:
//   - { detail: "message" }
//   - { detail: [{ code, message }, ...] }  (some DRF exception handlers)
//   - "plain string body"
//   - ["array", "of", "messages"]
//   - { field_name: ["error 1", "error 2"], other_field: "error" }  (field-level / serializer errors)
//   - { non_field_errors: [...] }
//   - { code: "STALE_REVISION", detail: "...", conflict_batch_id: 123 }  (domain-specific codes)
//
// ASSESSMENT.md flags that reading only error.response.data.detail silently drops FEFO/expiry/
// stale-revision reasons that come back as arrays or field maps. This normalizer keeps everything
// so callers can show a summary line *and* per-field errors, and preserve domain codes like
// conflict_batch_id / code so the UI can react (e.g. offer "refresh & create new revision").

function stringifyValue(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(stringifyValue).filter(Boolean).join('; ');
  if (typeof value === 'object') {
    if (value.message) return stringifyValue(value.message);
    if (value.detail) return stringifyValue(value.detail);
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

const KNOWN_NON_FIELD_KEYS = new Set(['detail', 'non_field_errors', 'message', 'code', 'error']);

export function normalizeApiError(error, fallbackMessage = 'Request failed.') {
  const response = error?.response;
  const data = response?.data;
  const status = response?.status ?? null;

  const result = {
    status,
    summary: fallbackMessage,
    fieldErrors: {},
    code: null,
    conflictBatchId: null,
    raw: data ?? null,
  };

  if (!response) {
    // Network error / no response reached the server at all.
    result.summary = error?.message ? `Network error: ${error.message}` : fallbackMessage;
    return result;
  }

  if (data == null) {
    return result;
  }

  if (typeof data === 'string') {
    result.summary = data || fallbackMessage;
    return result;
  }

  if (Array.isArray(data)) {
    const messages = data.map(stringifyValue).filter(Boolean);
    result.summary = messages.join(' | ') || fallbackMessage;
    return result;
  }

  if (typeof data === 'object') {
    if (data.code) result.code = data.code;
    if (data.conflict_batch_id != null) result.conflictBatchId = data.conflict_batch_id;

    const summaryParts = [];
    if (data.detail) summaryParts.push(stringifyValue(data.detail));
    if (data.non_field_errors) summaryParts.push(stringifyValue(data.non_field_errors));
    if (data.message && !data.detail) summaryParts.push(stringifyValue(data.message));

    Object.entries(data).forEach(([key, value]) => {
      if (KNOWN_NON_FIELD_KEYS.has(key) || key === 'conflict_batch_id') return;
      const message = stringifyValue(value);
      if (message) result.fieldErrors[key] = message;
    });

    if (summaryParts.length) {
      result.summary = summaryParts.join(' | ');
    } else if (Object.keys(result.fieldErrors).length) {
      result.summary = Object.entries(result.fieldErrors)
        .map(([field, message]) => `${field}: ${message}`)
        .join(' | ');
    } else {
      result.summary = fallbackMessage;
    }
    return result;
  }

  return result;
}

export default normalizeApiError;
